// Wave AW.3-React (2026-05-16) — 외부 바이어 경매 상세 화면
// API: GET /buyer-app/auction/:id

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuctionBuyerBidModal from './AuctionBuyerBidModal';
import AuthImage from './AuthImage';

function formatKrw(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString() + '원';
}
function formatMileage(km) {
  if (km == null) return '';
  return Number(km).toLocaleString() + ' km';
}
function formatRemaining(endsAt) {
  if (!endsAt) return '';
  var end = new Date(endsAt).getTime();
  var diff = end - Date.now();
  if (diff <= 0) return '마감됨';
  var d = Math.floor(diff / 86400000);
  var h = Math.floor((diff % 86400000) / 3600000);
  var m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return d + '일 ' + h + '시간';
  if (h > 0) return h + '시간 ' + m + '분';
  return m + '분';
}

function AuctionBuyerAuctionDetail() {
  var navigate = useNavigate();
  var params = useParams();
  var auctionId = params.id;

  var [auction, setAuction] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [bidModalOpen, setBidModalOpen] = useState(false);
  var [photoIdx, setPhotoIdx] = useState(0);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId, {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data && d.data.auction) {
          setAuction(d.data.auction);
        } else {
          setError((d && d.error) || '경매를 찾을 수 없습니다');
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError('네트워크 오류');
        setLoading(false);
      });
  }, [auctionId, navigate]);

  useEffect(function() { load(); }, [load]);

  if (loading) {
    return (
      <AuctionBuyerLayout activeKey="auctions">
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>로딩 중...</div>
      </AuctionBuyerLayout>
    );
  }
  if (error || !auction) {
    return (
      <AuctionBuyerLayout activeKey="auctions">
        <div style={{ textAlign: 'center', padding: 80, color: '#dc2626' }}>
          {error || '경매를 찾을 수 없습니다'}
          <div style={{ marginTop: 16 }}>
            <button type="button" onClick={function(){navigate('/buyer/auctions');}}
              style={{ ...btnStyle, background: '#1B3A5C', color: 'white' }}>
              목록으로
            </button>
          </div>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var car = auction.car || {};
  var liveStats = auction.liveStats || {};
  var myStatus = auction.myStatus || {};
  var inspection = auction.inspection || {};
  var seller = auction.seller || {};

  var photoCount = (auction.photos && auction.photos.length) || 0;
  var photoPath = photoCount > 0 ? ('/buyer-app/auction/' + auction.id + '/photo/' + photoIdx) : null;

  var existingBid = myStatus.myAmount || auction._my_bid_amount
    ? {
        amount: myStatus.myAmount || auction._my_bid_amount,
        status: myStatus.myStatus || auction._my_bid_status,
        updateCount: auction._my_bid_update_count || 0,
      }
    : null;
  var hasActiveBid = existingBid && existingBid.status === 'active';

  return (
    <AuctionBuyerLayout activeKey="auctions">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 100px' }}>
        {/* 뒤로 가기 */}
        <button type="button" onClick={function(){navigate('/buyer/auctions');}}
          style={{
            background: 'none', border: 'none', color: '#64748b',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            padding: '8px 0', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 4,
            fontFamily: "'Pretendard',sans-serif",
          }}>
          ← 경매 목록
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 24, alignItems: 'start' }}>
          {/* 좌측 — 사진 갤러리 */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {photoPath ? (
                <AuthImage path={photoPath} alt=""
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                />
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                  사진 없음
                </div>
              )}
              {photoCount > 1 && (
                <div style={{ display: 'flex', gap: 4, padding: 8, overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                  {Array.from({ length: photoCount }).map(function(_, i) {
                    return (
                      <button key={i} type="button" onClick={function(){setPhotoIdx(i);}}
                        style={{
                          flex: '0 0 80px', width: 80, height: 60,
                          padding: 0, border: photoIdx === i ? '2px solid #1B3A5C' : '1px solid #e2e8f0',
                          borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                          background: 'white',
                        }}>
                        <AuthImage path={'/buyer-app/auction/' + auction.id + '/photo/' + i}
                          alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 차량 정보 */}
            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                {car.year ? car.year + ' ' : ''}{car.model || auction.title || '경매 차량'}
              </h2>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                {car.number && <span>차량번호: <strong style={{ color: '#0f172a' }}>{car.number}</strong></span>}
                {car.mileage != null && <span>주행거리: <strong style={{ color: '#0f172a' }}>{formatMileage(car.mileage)}</strong></span>}
                {car.fuelType && <span>연료: <strong style={{ color: '#0f172a' }}>{car.fuelType}</strong></span>}
                {car.color && <span>색상: <strong style={{ color: '#0f172a' }}>{car.color}</strong></span>}
              </div>

              {/* 검차 요약 */}
              {inspection.summary && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>검차 요약</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: inspection.summaryColor || '#0f172a' }}>{inspection.summary}</div>
                </div>
              )}

              {/* 설명 */}
              {auction.description && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>상세 설명</div>
                  <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{auction.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* 우측 — 가격/입찰 sticky 카드 */}
          <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>현재 최고가</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1B3A5C', marginTop: 4 }}>
              {auction.highestBid > 0 ? formatKrw(auction.highestBid) : <span style={{ color: '#94a3b8', fontSize: 18 }}>입찰 없음</span>}
            </div>

            <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>입찰</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{liveStats.bidCount || 0}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>남은 시간</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{formatRemaining(auction.endsAt)}</div>
              </div>
            </div>

            {hasActiveBid && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, marginTop: 16 }}>
                <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>내 입찰</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af', marginTop: 2 }}>
                  {formatKrw(existingBid.amount)}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4 }}>
                  수정 {existingBid.updateCount}/1
                </div>
              </div>
            )}

            <button type="button" onClick={function(){setBidModalOpen(true);}}
              disabled={existingBid && existingBid.updateCount >= 1}
              style={{
                width: '100%', height: 52,
                background: (existingBid && existingBid.updateCount >= 1)
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: (existingBid && existingBid.updateCount >= 1) ? 'not-allowed' : 'pointer',
                marginTop: 20,
                boxShadow: (existingBid && existingBid.updateCount >= 1) ? 'none' : '0 4px 12px rgba(27,58,92,0.2)',
                fontFamily: "'Pretendard',sans-serif",
              }}>
              {(existingBid && existingBid.updateCount >= 1) ? '수정 한도 도달' : (hasActiveBid ? '입찰 수정' : '입찰하기')}
            </button>

            {/* 출품자 정보 */}
            {seller.displayName && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>출품자</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{seller.displayName}</div>
                {seller.mannerTemperature != null && (
                  <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4 }}>
                    매너온도 {seller.mannerTemperature}°
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 모바일 sticky 하단 버튼 — 작은 화면 전용 (선택, 후속) */}

        {bidModalOpen && (
          <AuctionBuyerBidModal auction={auction} existingBid={hasActiveBid ? existingBid : null}
            onClose={function(){setBidModalOpen(false);}}
            onSuccess={function(){ load(); }}
          />
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

var btnStyle = {
  padding: '10px 20px', borderRadius: 8, border: 'none',
  fontSize: 14, fontWeight: 700, cursor: 'pointer',
  fontFamily: "'Pretendard',sans-serif",
};

export default AuctionBuyerAuctionDetail;
